import type { Properties } from 'js-java-properties'
import { getProperty, setProperty } from 'js-java-properties'

import {
  LEGACY_SERIAL_PORT_CONFIG_DEFAULTS,
  SERIAL_DATA_BITS,
  SERIAL_PORT_CONFIG_DEFAULTS,
} from '@/features/controller/configuration/models/raspiProperties/defaults'
import type { ConfigWithErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import { extractErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import type {
  LegacySerialPortConfig,
  SerialPortConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { SerialPort, SerialPortParity } from '@/features/controller/configuration/models/raspiProperties/types'
import {
  readEnumProperty,
  readPositiveNumberProperty,
} from '@/features/controller/configuration/models/raspiProperties/utils'

const PROP_SERIAL_PORT_NAME = 'serialPort.name'
const DEV_PREFIX = '/dev/'
const SERIAL_PORT_1_NAME = '/dev/ttyS6'
const SERIAL_PORT_2_NAME = '/dev/ttyS7'

const PROP_SERIAL_PORT_BAUD_RATE = 'serialPort.baudRate'
const PROP_SERIAL_PORT_DATA_BITS = 'serialPort.dataBits'
const PROP_SERIAL_PORT_PARITY = 'serialPort.parity'
const PROP_SERIAL_PORT_STOP_BITS = 'serialPort.stopBits'

/**
 * A port as the file may name it. Named clients take a bare device name (`ttyS7`), while the legacy
 * `serialPort.name` uses the full path; the controller normalizes both, so both are read here, and
 * an unusable name is quoted back in the form the file wrote it.
 */
export function readBareOrFullSerialPortName(
  propertyValue: string | undefined,
): ConfigWithErrors<SerialPort | undefined> {
  const fullName =
    propertyValue !== undefined && !propertyValue.startsWith(DEV_PREFIX) ? DEV_PREFIX + propertyValue : propertyValue
  switch (fullName) {
    case SERIAL_PORT_1_NAME:
      return { config: SerialPort.port1 }
    case SERIAL_PORT_2_NAME:
      return { config: SerialPort.port2 }
    case undefined:
      return { config: undefined }
    default:
      return {
        config: SerialPort.port1,
        errors: [`Invalid serial port name '${propertyValue}'; the next save will replace it with port 1`],
      }
  }
}

/** Bare is the documented convention for a named client's own port key. */
export function writeBareSerialPortName(serialPort: SerialPort) {
  return getSerialPortName(serialPort).substring(DEV_PREFIX.length)
}

export function getSerialPortName(serialPort: SerialPort) {
  switch (serialPort) {
    case SerialPort.port1:
      return SERIAL_PORT_1_NAME
    case SerialPort.port2:
      return SERIAL_PORT_2_NAME
  }
}

const SERIAL_MODE_PATTERN = /^(\d)([NEO])(\d)$/
const PARITY_SYMBOLS: Record<SerialPortParity, string> = {
  [SerialPortParity.none]: 'N',
  [SerialPortParity.even]: 'E',
  [SerialPortParity.odd]: 'O',
}

/**
 * The framing as a single token, e.g. `8E1`: data bits, a parity letter, then stop bits. This is
 * `SerialMode.toString()` on the controller, and how a named client declares its `mode`, whereas the
 * legacy `serialPort.*` keys spell the same three fields out separately.
 */
export function writeSerialMode({ dataBits, parity, stopBits }: SerialPortConfig) {
  return `${dataBits}${PARITY_SYMBOLS[parity]}${stopBits}`
}

export function readSerialMode(
  value: string,
): ConfigWithErrors<Pick<SerialPortConfig, 'dataBits' | 'parity' | 'stopBits'>> {
  const match = SERIAL_MODE_PATTERN.exec(value)
  if (!match) {
    return {
      config: SERIAL_PORT_CONFIG_DEFAULTS,
      errors: [`Invalid Modbus serial mode '${value}', expected something like 8E1`],
    }
  }
  const [, dataBitsRaw, paritySymbol, stopBitsRaw] = match
  const parity = Object.values(SerialPortParity).find((candidate) => PARITY_SYMBOLS[candidate] === paritySymbol)!
  const dataBits = Number(dataBitsRaw)
  const stopBits = Number(stopBitsRaw)
  const errors: string[] = []
  if (dataBits !== SERIAL_DATA_BITS) {
    // Parses on the controller, but fails when the serial port is actually opened
    errors.push(`Modbus serial mode '${value}' uses ${dataBits} data bits; only ${SERIAL_DATA_BITS} is supported`)
  }
  if (stopBits !== 1 && stopBits !== 2) {
    errors.push(`Modbus serial mode '${value}' uses ${stopBits} stop bits; only 1 or 2 are supported`)
  }
  return { config: { dataBits, parity, stopBits }, errors }
}

export function readLegacySerialPortConfig(properties: Properties): ConfigWithErrors<LegacySerialPortConfig> {
  const errors: string[] = []
  const serialPort =
    extractErrors(readBareOrFullSerialPortName(getProperty(properties, PROP_SERIAL_PORT_NAME)), errors) ||
    LEGACY_SERIAL_PORT_CONFIG_DEFAULTS.serialPort
  return {
    config: {
      portConfig: {
        // The row these land on is the controller itself, so each label has to name the setting
        baudRate: extractErrors(
          readPositiveNumberProperty(
            properties,
            PROP_SERIAL_PORT_BAUD_RATE,
            'serial port baud rate',
            SERIAL_PORT_CONFIG_DEFAULTS.baudRate,
          ),
          errors,
        ),
        dataBits: extractErrors(
          readPositiveNumberProperty(
            properties,
            PROP_SERIAL_PORT_DATA_BITS,
            'serial port data bits',
            SERIAL_PORT_CONFIG_DEFAULTS.dataBits,
          ),
          errors,
        ),
        parity: extractErrors(
          readEnumProperty(
            properties,
            PROP_SERIAL_PORT_PARITY,
            'serial port parity',
            SerialPortParity,
            SERIAL_PORT_CONFIG_DEFAULTS.parity,
          ),
          errors,
        ),
        stopBits: extractErrors(
          readPositiveNumberProperty(
            properties,
            PROP_SERIAL_PORT_STOP_BITS,
            'serial port stop bits',
            SERIAL_PORT_CONFIG_DEFAULTS.stopBits,
          ),
          errors,
        ),
      },
      serialPort,
    },
    errors,
  }
}

export function writeLegacySerialPortConfig(properties: Properties, config: LegacySerialPortConfig) {
  setProperty(properties, PROP_SERIAL_PORT_BAUD_RATE, config.portConfig.baudRate?.toString())
  setProperty(properties, PROP_SERIAL_PORT_DATA_BITS, config.portConfig.dataBits?.toString())
  setProperty(properties, PROP_SERIAL_PORT_PARITY, config.portConfig.parity?.toString())
  setProperty(properties, PROP_SERIAL_PORT_STOP_BITS, config.portConfig.stopBits?.toString())
  setProperty(properties, PROP_SERIAL_PORT_NAME, config.serialPort === SerialPort.port2 ? SERIAL_PORT_2_NAME : null)
}
